import { useState } from 'react';
import { api } from '../lib/api.ts';

/**
 * Who is signed in, and a way to change how that reads.
 *
 * Editing happens in place rather than behind a settings screen: three short
 * fields do not justify a screen of their own, and a screen nobody opens is a
 * name nobody fixes. People invited into a company never gave a name at all, so
 * for them this starts as an email address and is the only way to change it.
 *
 * Username is optional and wins on screen when set - "pete" reads better than
 * "Pete Atkins" on a workshop display shared by a shift.
 */
export function WhoAmI({
  name, email, role, firstName, lastName, username,
}: {
  name: string | null; email: string | null; role: string | null;
  firstName: string | null; lastName: string | null; username: string | null;
}) {
  const [current, setCurrent] = useState(name);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    firstName: firstName ?? '', lastName: lastName ?? '', username: username ?? '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.updateProfile(draft);
      setCurrent(r.username || r.name);
      setEditing(false);
    } catch {
      // Leave the field open with what they typed still in it, rather than
      // closing and silently discarding the edit.
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    // Three fields in a row rather than a dialog. Username wins on screen when
    // it is set, so it sits last and says so.
    return (
      <span className="who who-edit">
        <input
          className="who-input" autoFocus disabled={busy} maxLength={80} placeholder="First"
          value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
        />
        <input
          className="who-input" disabled={busy} maxLength={80} placeholder="Last"
          value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
        />
        <input
          className="who-input" disabled={busy} maxLength={80} placeholder="Username (optional)"
          title="Shown instead of your name when set"
          value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value.replace(/\s/g, '') })}
        />
        <button className="tiny" disabled={busy} onClick={save}>save</button>
        <button className="tiny ghost" disabled={busy} onClick={() => setEditing(false)}>cancel</button>
      </span>
    );
  }

  return (
    <span className="who">
      <button
        className="who-name"
        title={`Signed in as ${email}. Click to change how your name appears.`}
        onClick={() => setEditing(true)}
      >
        {current || email}
      </button>
      <span className="pill">{role}</span>
    </span>
  );
}
